import * as React from 'react';
import * as ReactDOM from 'react-dom';
import cancelEvent from '../../../services/mini/cancelEvent';
import TextFieldWrapper from '../TextFieldWrapper/TextFieldWrapper';

type AutoCompleteProps = {
    minQueryLength: number;
    placeholder: string;
    onSearch: ({ query }: { query: string }) => Promise<any[]>;
    onSelect: (item: any) => void;
    height?: string;
    value?: string;
    inputClassName?: string;
    searchLabel?: string;
    searchIcon?: boolean;
    allowCustomText?: boolean;
    maxItemsLimit?: number;
    searchMoreTooltip?: string;
}

type AutoCompleteState = {
    query: string;
    items: any[];
    showList: boolean
    selectedItemIndex: number
}

class AutoComplete extends React.Component<AutoCompleteProps, AutoCompleteState> {
    _isMounted: boolean;

    constructor(props, context) {
        super(props, context);

        this.state = {
            query: this.props.value || '',
            items: [],
            selectedItemIndex: -1,
            showList: false
        };

        this.handleClickOutsideCb = this.handleClickOutsideCb.bind(this);
    }

    componentDidMount() {
        this._isMounted = true;
        document.addEventListener('click', this.handleClickOutsideCb, false);
    }

    componentWillUnmount() {
        this._isMounted = false;
        document.removeEventListener('click', this.handleClickOutsideCb, false);
    }

    handleClickOutsideCb(e) {
        if (!this.state.showList || !this._isMounted) return;
        var domNode = ReactDOM.findDOMNode(this);
        var clickedEl = e.target;

        if ((!domNode || !domNode.contains(clickedEl))) {
            this.close();
        }
    }

    close = () => {
        this.setState({ showList: false });
    }

    onTextInputChange = (e) => {
        const field = e.target.name;
        const fieldValue = e.target.value;
        const newState = { ...this.state };
        const { onSearch, minQueryLength, allowCustomText } = this.props;
        newState[field] = fieldValue;
        this.setState(newState);

        if (fieldValue.length > minQueryLength) {
            onSearch({ query: fieldValue }).then((items) => {
                this.setState({
                    items,
                    showList: true,
                    selectedItemIndex: -1
                });
            });
        }
    }

    onTextInputBlur = () => {
        const { query, items } = this.state;
        const { value, allowCustomText, onSelect } = this.props;
        const selectedFromList = items.find(el => el.name === query);

        if (allowCustomText === true) {
            onSelect({ key: query });
        } else {
            if (!selectedFromList) {
                this.setState({ query: (value || '') });
            }
        }
    }

    onKeyDown = (e) => {
        const keyCode = e.keyCode || e.which;

        if (keyCode === 38 || keyCode === 40) {
            this.switchSelectedItem(keyCode);
            cancelEvent(e);
            return;
        }

        if (keyCode === 13) {
            this.selectItemByIndex();
            cancelEvent(e);
            return;
        }
    }

    switchSelectedItem = (keyCode) => {
        const {
            selectedItemIndex,
            items,
            showList
        } = this.state;
        let nextSelectedItemIndex = selectedItemIndex;

        if (!showList) return;

        if (keyCode === 40) { //arrow bottom
            nextSelectedItemIndex++;
            if (nextSelectedItemIndex > items.length - 1) {
                nextSelectedItemIndex = 0;
            }
        }
        if (keyCode === 38) { //arrow top
            nextSelectedItemIndex--;
            if (nextSelectedItemIndex < 0) {
                nextSelectedItemIndex = items.length - 1;
            }
        }
        this.setState({
            selectedItemIndex: nextSelectedItemIndex
        }, () => {
            const itemEl = this.refs[`item${nextSelectedItemIndex}`];
            if (itemEl) {
                (itemEl as any).scrollIntoViewIfNeeded();
            }
        });
    }

    selectItemByIndex = () => {
        const { selectedItemIndex, items } = this.state;
        if (items[selectedItemIndex]) {
            this.selectItem(items[selectedItemIndex]);
        }
    }

    selectItem = (item) => {
        const { onSelect } = this.props;
        this.setState({ showList: false, query: '', selectedItemIndex: -1 });
        onSelect(item);
    }

    render() {
        const { placeholder, height, searchIcon, searchLabel, maxItemsLimit, searchMoreTooltip, inputClassName, allowCustomText } = this.props;
        const { query, items, showList, selectedItemIndex } = this.state;
        const itemHeight = 32;
        const style = {
            height: `${items.length * itemHeight}px`,
        };

        return <div className="autocomplete">
            {searchIcon !== false && <div className="autocomplete__label">
                <i className="icon icon-alan-search" /> Search
            </div>}
            <TextFieldWrapper
                inputClassName={inputClassName}
                placeholder={placeholder}
                label={searchLabel !== undefined ? searchLabel : "Search"}
                name="query"
                id="query"
                value={query}
                autoComplete="off"
                onFocus={this.onTextInputChange}
                onChange={this.onTextInputChange}
                onKeyDown={this.onKeyDown}
                onEsc={this.close}
                skipOnChangeAfterOnBlur={true}
                onBlur={this.onTextInputBlur}
                height={height} />

            {(items.length > 0 && showList) &&
                <div className="autocomplete__list" style={style}>
                    {items.map((item, index) => {
                        return <div
                            key={index}
                            ref={`item${index}`}
                            onClick={() => { this.selectItem(item); }}
                            className={`autocomplete__item ${selectedItemIndex === index && 'selected'}`}>
                            {item.name}
                        </div>;
                    })}
                    {maxItemsLimit < items.length ?
                        <div
                            className={`autocomplete__tooltip`}>
                            {searchMoreTooltip}
                        </div> : <></>}
                </div>}
            {(items.length === 0 && showList && allowCustomText !== true) &&
                <div className="autocomplete__list">
                    <div
                        className={`autocomplete__tooltip`}>
                        No options
                    </div>
                </div>}
        </div>;
    }
}

export default AutoComplete;